import React, { useContext } from 'react';
import { isRTLLocale } from '@wix/cashier-common/dist/src/utils/isRTLLocale';
import { PaymentProvider } from '@wix/cashier-common/dist/src/enums/payments/PaymentProvider';
import { getSupportedCardBrands } from '@wix/cashier-common/dist/src/utils/getSupportedCardBrands';
import { AppStateContext } from '../../contexts/AppState';
import { DeviceTypeContext } from '../../contexts/DeviceType';
import { LocaleContext } from '../../contexts/Locale';
import { useTheme } from '../../contexts/Theme';
import { PaymentProviderContext } from '../../contexts/PaymentProvider';
import { type DeviceType } from '../../types/DeviceType';
import { FieldName } from '../../types/CardFieldName';
import { CardCVV } from '../fields/CardCVV';
import { CardExpireDate } from '../fields/CardExpireDate';
import { CardHolderName } from '../fields/CardHolderName';
import { CardNumber } from '../fields/CardNumber';
import { CardPostalCode } from '../fields/CardPostalCode';
import { t } from '../../utils/i18n';
import { Container, Fields, FieldsGroup, Form, Line } from './elements';
import { type CardFormViewProps } from './types';
import { providerFormViewMap } from './ProviderView/providerFormViewMap';
import { getSquareCardFormProps } from './ProviderView/Square/utils';
import { RedesignedFieldsGroup, RedesignedForm, Text } from './elements/redesign';

export const CardFormView = (props: CardFormViewProps) => {
  const {
    fields,
    cardNetwork,
    formRefs,

    onFieldChange,
    onFieldBlur,
    onFieldFocus,
    onFieldLabelClick,
  } = props;

  const theme = useTheme();
  const deviceType: DeviceType = useContext(DeviceTypeContext);
  const locale: string = useContext(LocaleContext);
  const { isHidden, omitOffset, isFullWidth } = useContext(AppStateContext);
  const paymentProvider = useContext(PaymentProviderContext);

  const fieldsToRender: FieldName[] = Object.keys(fields) as FieldName[];
  const cardNetworkBrandsList = getSupportedCardBrands(paymentProvider);
  const isSavedCardWithCVV = fieldsToRender.length === 1 && fieldsToRender.includes(FieldName.CVV);
  const customInput = theme === 'wsr-like' || paymentProvider === PaymentProvider.Square;
  const getDefaultFieldProps = (fieldName: FieldName) => ({
    ...fields[fieldName],
    ref: formRefs[fieldName],
    customInput,
    onChange: (value, cb?, allowAutoBlurIfValid?) => onFieldChange(fieldName, value, cb, allowAutoBlurIfValid),
    onBlur: (e?: any, isAutoFilled?: boolean) => onFieldBlur(fieldName, isAutoFilled),
    onFocus: (e?: any, isAutoFilled?: boolean) => onFieldFocus(fieldName, isAutoFilled),
    onLabelClick: () => onFieldLabelClick(fieldName),
  });

  const renderContent = () => {
    if (paymentProvider === PaymentProvider.Square) {
      const ProviderView = providerFormViewMap[paymentProvider];
      return <ProviderView fields={getSquareCardFormProps({ fieldsToRender, fields, onFieldBlur, onFieldFocus })} />;
    }

    return (
      <Fields>
        {fieldsToRender.includes(FieldName.Number) && (
          <Line>
            <CardNumber
              {...getDefaultFieldProps(FieldName.Number)}
              cardNetwork={cardNetwork}
              cardNetworkBrandsList={cardNetworkBrandsList}
            />
          </Line>
        )}

        <FieldsGroup>
          {fieldsToRender.includes(FieldName.ExpireDate) && (
            <CardExpireDate {...getDefaultFieldProps(FieldName.ExpireDate)} />
          )}
          {fieldsToRender.includes(FieldName.CVV) && (
            <CardCVV {...getDefaultFieldProps(FieldName.CVV)} cardNetwork={cardNetwork} />
          )}
        </FieldsGroup>

        {fieldsToRender.includes(FieldName.HolderName) && (
          <Line>
            <CardHolderName {...getDefaultFieldProps(FieldName.HolderName)} />
          </Line>
        )}

        {fieldsToRender.includes(FieldName.PostalCode) && (
          <Line>
            <CardPostalCode {...getDefaultFieldProps(FieldName.PostalCode)} />
          </Line>
        )}
      </Fields>
    );
  };

  return (
    <Container
      theme={theme}
      deviceType={deviceType}
      isRTL={isRTLLocale(locale)}
      isCollapsed={fieldsToRender.length === 0}
      providerId={paymentProvider}
      customInput={customInput}
    >
      {customInput ? (
        <Form isHidden={isHidden} isFullWidth={isFullWidth} omitOffset={omitOffset}>
          {renderContent()}
        </Form>
      ) : (
        <RedesignedForm isHidden={isHidden} theme={theme}>
          {fieldsToRender.includes(FieldName.Number) && (
            <CardNumber
              {...getDefaultFieldProps(FieldName.Number)}
              cardNetwork={cardNetwork}
              cardNetworkBrandsList={cardNetworkBrandsList}
            />
          )}

          {isSavedCardWithCVV && <Text>{t('card.cvv.singleField', { cvvLabel: t('card.cvv.cvc') })}</Text>}

          <RedesignedFieldsGroup>
            {fieldsToRender.includes(FieldName.ExpireDate) && (
              <CardExpireDate {...getDefaultFieldProps(FieldName.ExpireDate)} />
            )}
            {fieldsToRender.includes(FieldName.CVV) && (
              <CardCVV {...getDefaultFieldProps(FieldName.CVV)} cardNetwork={cardNetwork} />
            )}
          </RedesignedFieldsGroup>

          {fieldsToRender.includes(FieldName.HolderName) && (
            <CardHolderName {...getDefaultFieldProps(FieldName.HolderName)} />
          )}

          {fieldsToRender.includes(FieldName.PostalCode) && (
            <CardPostalCode {...getDefaultFieldProps(FieldName.PostalCode)} />
          )}
        </RedesignedForm>
      )}
    </Container>
  );
};
